import style from "./CourseProgressBanner.module.css";
import { Link } from "react-router-dom";
import VideoBreadcrumb from "@/ui/videoBreadcrumb/VideoBreadcrumb";
import Button from "@/ui/button/Button";
import Icon from "@/ui/icon/Icon";
import ContinueWatchingStatistics from "@/features/courses/components/continueWatchingStatistics/ContinueWatchingStatistics";
import { formatTime } from "@/utils/helpers";
import UpgradeToPremium from "@/features/auth/components/upgradeToPremium/UpgradeToPremium";
import { useUser } from "@/features/auth/hooks/useUser";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useLocalized } from "@/i18n/useLocalized";

import AuthPrompt from "@/features/auth/components/authPrompt/AuthPrompt";

function videoPercentComplete(video) {
	if (video?.user_activity?.current_second_in_video && video?.duration > 0) {
		return Math.floor(
			(100 * video.user_activity.current_second_in_video) / video.duration
		);
	}
	return 0;
}

function CourseProgressBanner({ courseData }) {
	const video = courseData?.video;
	const { user, isLoggedIn } = useUser();
	const [showProgress, setShowProgress] = useState(true);
	const isMobile = useMediaQuery("(max-width: 800px)");
	const { t } = useTranslation();
	const l = useLocalized();

	useEffect(() => {
		if (!user?.is_subscribed) {
			setShowProgress(Math.random() < 0.5);
		}
	}, [user?.is_subscribed]);

	if (!video) {
		return null;
	}

	const videoLink = `/courses/${video.module.course.slug}/${video.module.slug}/${video.slug}/`;
	const currentSecond = video?.user_activity?.current_second_in_video || 0;
	const heading = currentSecond
		? t("courses:banner.resume")
		: t("courses:banner.start");
	const buttonLabel = currentSecond
		? t("courses:banner.resumeFrom", {
				time: `${Math.floor(currentSecond / 60)}:${String(
					Math.floor(currentSecond % 60)
				).padStart(2, "0")}`,
			})
		: t("courses:banner.start");

	const sideContent = !isLoggedIn ? (
		<AuthPrompt />
	) : video?.user_activity?.current_second_in_video === 0 &&
	  !user?.is_subscribed ? (
		<UpgradeToPremium />
	) : user?.is_subscribed ? (
		<ContinueWatchingStatistics
			courseProgress={courseData.courseProgress}
		/>
	) : showProgress ? (
		<ContinueWatchingStatistics
			courseProgress={courseData.courseProgress}
		/>
	) : (
		<UpgradeToPremium />
	);

	if (isMobile) {
		return (
			<div className={style.mobileBanner}>
				<h2 className={style.mobileHeader}>
					<span className={style.mobileHeaderDot} />
					{heading}
				</h2>
				<div className={style.mobileCard}>
					<div className={style.mobileWhiteCard}>
						<VideoBreadcrumb video={video} />
						<Link
							to={videoLink}
							style={{
								"--background": `url(${video.thumbnail})`,
								"--video-percent": `${videoPercentComplete(
									video
								)}%`,
							}}
							className={`${style.video} ${style.mobileVideo}`}
						>
							<div className={style.mobileTimePills}>
								<span>
									{formatTime(
										video?.user_activity
											?.current_second_in_video || 0
									)}
								</span>
								<span>{formatTime(video?.duration || 0)}</span>
							</div>
							{video?.user_activity?.current_second_in_video >
								0 && (
								<div className={style.mobileProgressBar} />
							)}
						</Link>
						<h1 className={style.mobileTitle}>
							{l(video, "name")}
						</h1>
						<p className={style.mobileDescription}>
							{l(video, "description") ||
								l(video.module, "description")}
						</p>
					</div>
					<div className={style.mobileFooter}>
						{sideContent}
						<Button
							to={videoLink}
							type="accent"
							className={style.mobileButton}
						>
							<Icon icon="fa-play" />
							<span>{buttonLabel}</span>
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className={`${style.banner} ${user?.is_subscribed ? style.subscribed : ""}`}
		>
			<div className={style.left}>
				<h2>{heading}</h2>
				<h1>{l(video, "name")}</h1>
				<p>
					{l(video, "description") || l(video.module, "description")}
				</p>
				<div className={style.buttonHolder}>
					<Button to={videoLink} type="accent">
						<Icon icon="fa-play" />
						<span>{buttonLabel}</span>
					</Button>
				</div>
			</div>
			<div className={style.right}>
				<div className={style.videoInfo}>
					<VideoBreadcrumb video={video} />
					<Link
						to={videoLink}
						style={{
							"--background": `url(${video.thumbnail})`,
							"--video-percent": `${videoPercentComplete(
								video
							)}%`,
						}}
						className={style.video}
					>
						{video?.user_activity?.current_second_in_video > 0 && (
							<div className={style.progress}>
								<div className={style.progressInfo}>
									<span className={style.current}>
										{formatTime(
											video?.user_activity
												?.current_second_in_video || 0
										)}
									</span>
									<span className={style.duration}>
										<span>
											{formatTime(
												video?.duration -
													(video?.user_activity
														?.current_second_in_video ||
														0)
											)}
										</span>
										<Icon icon="fa-arrow-right-to-line" />
									</span>
								</div>
							</div>
						)}
					</Link>
				</div>

				{sideContent}
			</div>
		</div>
	);
}

export default CourseProgressBanner;
